import type { StudentLearningProfile } from './types'
import { createNeutralPreferenceProfile } from './createNeutralPreferenceProfile'

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object'
}

function derivePretestScore(ability: StudentLearningProfile['ability']): number {
  const evidenced = Object.values(ability).filter((item) => item.sampleCount > 0)
  if (evidenced.length === 0) return 0
  const average = evidenced.reduce((sum, item) => sum + item.score, 0) / evidenced.length
  return Math.max(0, Math.min(10, Math.round(average / 10)))
}

export function migrateStudentLearningProfile(value: unknown): StudentLearningProfile | null {
  if (!isRecord(value)) return null
  if (!isRecord(value.identity) || !isRecord(value.ability) || !isRecord(value.preference)) return null
  if (typeof value.updatedAt !== 'string') return null

  const ability = value.ability as unknown as StudentLearningProfile['ability']
  const pretestScore = typeof value.pretestScore === 'number'
    ? Math.max(0, Math.min(10, value.pretestScore))
    : derivePretestScore(ability)
  const recommendedLevel = value.recommendedLevel === 'A' || value.recommendedLevel === 'B' || value.recommendedLevel === 'C'
    ? value.recommendedLevel
    : pretestScore >= 8 ? 'A' : pretestScore >= 4 ? 'B' : 'C'

  const rawPreference = value.preference as Partial<StudentLearningProfile['preference']>
  const presentation = {
    narrative: Number(rawPreference.presentation?.narrative ?? 0),
    visual: Number(rawPreference.presentation?.visual ?? 0),
    verbal: Number(rawPreference.presentation?.verbal ?? 0),
    taskBased: Number(rawPreference.presentation?.taskBased ?? 0),
  }
  const preference = rawPreference.tendencies && rawPreference.motivations && rawPreference.companionAffinity
    ? {
      ...createNeutralPreferenceProfile(presentation),
      ...rawPreference,
      presentation,
    }
    : createNeutralPreferenceProfile(presentation)

  return {
    identity: value.identity as unknown as StudentLearningProfile['identity'],
    ability,
    preference,
    knowledgePoints: Array.isArray(value.knowledgePoints)
      ? value.knowledgePoints as StudentLearningProfile['knowledgePoints']
      : [],
    pretestScore,
    recommendedLevel,
    confidence: typeof value.confidence === 'number' ? value.confidence : 0,
    version: 3,
    updatedAt: value.updatedAt,
  }
}
